import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, Edit, Trash2, Search, RefreshCw, 
  AlertCircle, X, Check, XCircle, Mail, Phone, Calendar,
  Shield, Clock, Copy, Eye, EyeOff, Key, CheckSquare, Square, UserPlus
} from 'lucide-react';
import { usersAPI, rolesAPI, branchesAPI, contractorLabsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import toast from 'react-hot-toast';

const StaffManagement = () => {
  const { user: currentUser, refreshPermissions } = useAuth();
  const { canAccessFeature } = useSubscription();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [contractorLabs, setContractorLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Permissions state
  const [allPages, setAllPages] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);
  
  // New role state
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRoleData, setNewRoleData] = useState({ name_ar: '', name_en: '', description: '' });
  const [savingRole, setSavingRole] = useState(false);
  const [deletingRole, setDeletingRole] = useState(false);
  
  // System roles that cannot be deleted
  const systemRoles = ['owner', 'lab_supervisor', 'lab_specialist', 'lab_accountant', 'contractor_lab'];
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role_id: '',
    branch_id: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Role display names
  const roleDisplayNames = {
    'owner': 'مدير النظام',
    'lab_supervisor': 'مشرف المختبر',
    'lab_specialist': 'أخصائي مختبر',
    'lab_accountant': 'محاسب',
    'contractor_lab': 'مختبر متعاقد'
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 100 };
      if (filterRole) params.role_id = filterRole;
      if (filterBranch) params.branch_id = filterBranch;
      
      const response = await usersAPI.getAll(params);
      const data = response.data?.data?.items || response.data?.items || response.data?.data || [];
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('حدث خطأ في تحميل البيانات');
      setUsers([]);
    }
    setLoading(false);
  }, [filterRole, filterBranch]);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await rolesAPI.getAll();
      const data = response.data?.data || [];
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await branchesAPI.getAll({ active: true, limit: 100 });
      const data = response.data?.data?.items || response.data?.items || response.data?.data || [];
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  }, []);

  const fetchContractorLabs = useCallback(async () => {
    try {
      const response = await contractorLabsAPI.getAll({ active: true, limit: 100 });
      const data = response.data?.data?.items || response.data?.items || response.data?.data || [];
      setContractorLabs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching contractor labs:', err);
    }
  }, []);

  const fetchAllPages = useCallback(async () => {
    try {
      const response = await rolesAPI.getAllPages();
      const data = response.data?.data || [];
      setAllPages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching pages:', err);
    }
  }, []);

  useEffect(() => { 
    fetchUsers();
    fetchRoles();
    fetchBranches();
    fetchContractorLabs();
    fetchAllPages();
  }, [fetchUsers, fetchRoles, fetchBranches, fetchContractorLabs, fetchAllPages]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role_id: '',
      branch_id: '',
      is_active: true
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'الاسم مطلوب';
    if (!formData.email.trim()) {
      errors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'البريد الإلكتروني غير صحيح';
    }
    if (!formData.role_id) errors.role_id = 'الدور مطلوب';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    // For contractor_lab role, use contractor_lab_id as branch_id for the form
    const isContractorLab = user.role_id?.name === 'contractor_lab';
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role_id: user.role_id?._id || '',
      branch_id: isContractorLab ? (user.contractor_lab_id?._id || '') : (user.branch_id?._id || ''),
      is_active: user.is_active !== false
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      // Check if selected role is contractor_lab
      const selectedRoleName = roles.find(r => r._id === formData.role_id)?.name;
      
      // Prepare data - if contractor_lab role, send branch_id value as contractor_lab_id
      const dataToSend = { ...formData };
      if (selectedRoleName === 'contractor_lab') {
        dataToSend.contractor_lab_id = formData.branch_id;
        dataToSend.branch_id = null; // Clear branch_id for contractor lab users
      }
      
      if (showEditModal && selectedUser) {
        await usersAPI.update(selectedUser._id, dataToSend);
        toast.success('تم تحديث بيانات الموظف بنجاح');
      } else {
        const response = await usersAPI.create(dataToSend);
        const password = response.data?.data?.generatedPassword;
        if (password) {
          setGeneratedPassword(password);
          setShowPasswordModal(true);
        }
        toast.success('تم إضافة الموظف بنجاح');
      }
      await fetchUsers();
      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setDeleting(true);
    try {
      await usersAPI.delete(selectedUser._id);
      setUsers(prev => prev.filter(u => u._id !== selectedUser._id));
      setShowDeleteModal(false);
      setSelectedUser(null);
      toast.success('تم حذف الموظف بنجاح');
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء الحذف');
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء الحذف');
    }
    setDeleting(false);
  };

  const handleToggleStatus = async (user) => {
    try {
      await usersAPI.updateStatus(user._id, !user.is_active);
      setUsers(prev => prev.map(u => 
        u._id === user._id ? { ...u, is_active: !u.is_active } : u
      ));
      toast.success(user.is_active ? 'تم إيقاف الحساب' : 'تم تفعيل الحساب');
    } catch (err) {
      toast.error('حدث خطأ أثناء تحديث الحالة');
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    toast.success('تم نسخ كلمة المرور');
  };

  // Permissions management
  const openPermissionsModal = async () => {
    setShowPermissionsModal(true);
    if (roles.length > 0) {
      // Select first non-owner role by default
      const defaultRole = roles.find(r => r.name !== 'owner') || roles[0];
      await loadRolePermissions(defaultRole);
    }
  };

  const loadRolePermissions = async (role) => {
    setSelectedRole(role);
    try {
      const response = await rolesAPI.getPermissions(role._id);
      const data = response.data?.data;
      setRolePermissions(data?.allowed_pages || []);
    } catch (err) {
      console.error('Error loading role permissions:', err);
      setRolePermissions([]);
    }
  };

  const togglePagePermission = (pageKey) => {
    if (selectedRole?.name === 'owner') return; // Cannot modify owner
    
    setRolePermissions(prev => {
      if (prev.includes(pageKey)) {
        return prev.filter(p => p !== pageKey);
      } else {
        return [...prev, pageKey];
      }
    });
  };

  // Filter pages by plan features
  const availablePages = allPages.filter(page => {
    if (!page.featureKey) return true;
    return canAccessFeature(page.featureKey);
  });

  const toggleAllPermissions = () => {
    if (selectedRole?.name === 'owner') return;

    if (rolePermissions.length === availablePages.length) {
      setRolePermissions([]);
    } else {
      setRolePermissions(availablePages.map(p => p.key));
    }
  };

  const savePermissions = async () => {
    if (!selectedRole || selectedRole.name === 'owner') return;
    
    setSavingPermissions(true);
    try {
      await rolesAPI.updatePermissions(selectedRole._id, rolePermissions);
      toast.success('تم حفظ الصلاحيات بنجاح');
      // Refresh permissions for current user if their role was modified
      if (currentUser?.role_id?._id === selectedRole._id || currentUser?.role_id === selectedRole._id) {
        await refreshPermissions();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء حفظ الصلاحيات');
    }
    setSavingPermissions(false);
  };

  // Add new role
  const handleAddRole = async () => {
    if (!newRoleData.name_ar.trim()) {
      toast.error('الاسم بالعربي مطلوب');
      return;
    }
    
    setSavingRole(true);
    try {
      const response = await rolesAPI.create({
        name: newRoleData.name_ar.trim(),
        name_ar: newRoleData.name_ar.trim(),
        name_en: newRoleData.name_en.trim() || newRoleData.name_ar.trim(),
        description: newRoleData.description.trim() || null,
        allowed_pages: []
      });
      
      toast.success('تم إضافة الدور بنجاح');
      setShowAddRoleModal(false);
      setNewRoleData({ name_ar: '', name_en: '', description: '' });
      
      // Refresh roles list
      await fetchRoles();
      
      // Select the new role
      const newRole = response.data?.data;
      if (newRole) {
        await loadRolePermissions(newRole);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء إضافة الدور');
    }
    setSavingRole(false);
  };

  // Delete role
  const handleDeleteRole = async () => {
    if (!selectedRole || systemRoles.includes(selectedRole.name)) {
      toast.error('لا يمكن حذف هذا الدور');
      return;
    }
    
    setDeletingRole(true);
    try {
      await rolesAPI.delete(selectedRole._id);
      toast.success('تم حذف الدور بنجاح');
      
      // Refresh roles list
      await fetchRoles();
      
      // Select first non-owner role
      const defaultRole = roles.find(r => r.name !== 'owner' && r._id !== selectedRole._id);
      if (defaultRole) {
        await loadRolePermissions(defaultRole);
      } else {
        setSelectedRole(null);
        setRolePermissions([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء حذف الدور');
    }
    setDeletingRole(false);
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.includes(searchQuery)
  );

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatLastLogin = (date) => {
    if (!date) return 'لم يسجل دخول';
    const now = new Date();
    const loginDate = new Date(date);
    const diff = now - loginDate;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    if (days < 7) return `منذ ${days} يوم`;
    return formatDate(date);
  };

  const inputClasses = (fieldName) => `w-full px-4 py-2.5 bg-white border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
    formErrors[fieldName] 
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500'
  }`;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
            <Users className="w-6 h-6 text-slate-100" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">إدارة الموظفين</h1>
            <p className="text-slate-500 text-sm mt-1">Staff Management</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={openPermissionsModal}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-xl font-medium text-sm hover:bg-slate-50 transition-all duration-200 shadow-sm"
          >
            <Key className="w-4 h-4" strokeWidth={2.5} />
            <span>صلاحيات الأدوار</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAdd}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            <span>إضافة موظف جديد</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="البحث بالاسم أو البريد..."
              className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
            />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
          
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
          >
            <option value="">جميع الأدوار</option>
            {roles.map(role => (
              <option key={role._id} value={role._id}>
                {roleDisplayNames[role.name] || role.name}
              </option>
            ))}
          </select>

          <select
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
          >
            <option value="">جميع الفروع</option>
            {branches.map(branch => (
              <option key={branch._id} value={branch._id}>{branch.branch_name}</option>
            ))}
          </select>

          <button onClick={fetchUsers} className="flex items-center justify-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-200">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3 text-amber-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="mr-auto"><X className="w-4 h-4" /></button>
        </motion.div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Users className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">لا يوجد موظفين</p>
            <button onClick={handleAdd} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl">إضافة موظف</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الموظف</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">البريد الإلكتروني</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الجوال</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الدور</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الفرع</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">آخر دخول</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <motion.tr
                    key={user._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 group"
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-slate-600">
                            {user.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">{user.name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{formatDate(user.created_at)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2 text-slate-700">
                        <Mail className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                        <span className="text-sm" dir="ltr">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2 text-slate-700">
                        <Phone className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                        <span className="font-mono text-sm" dir="ltr">{user.phone || '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide bg-blue-50 text-blue-700 border border-blue-200">
                        <Shield className="w-3 h-3" strokeWidth={2} />
                        {roleDisplayNames[user.role_id?.name] || user.role_id?.name || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-700">
                        {user.branch_id?.branch_name || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2 text-slate-600 text-sm">
                        <Clock className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                        <span>{formatLastLogin(user.last_login_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <button
                        onClick={() => handleToggleStatus(user)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide border transition-colors ${
                          user.is_active 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' 
                            : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        {user.is_active ? 'نشط' : 'معطل'}
                      </button>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`إعادة تعيين كلمة مرور "${user.name}"؟`)) return;
                            try {
                              const res = await usersAPI.resetPassword(user._id);
                              const data = res.data?.data || res.data;
                              toast.success(`كلمة المرور الجديدة: ${data.generatedPassword}`, { duration: 15000 });
                              navigator.clipboard?.writeText(data.generatedPassword);
                            } catch (err) {
                              toast.error(err.response?.data?.message || 'فشل إعادة تعيين كلمة المرور');
                            }
                          }}
                          className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all duration-150"
                          title="إعادة تعيين كلمة المرور"
                        >
                          <Key className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }}
                          className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-150"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {showEditModal ? 'تعديل بيانات الموظف' : 'إضافة موظف جديد'}
                </h3>
                <button onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="text-slate-400 hover:text-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الاسم <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    className={inputClasses('name')}
                    placeholder="الاسم الكامل"
                  />
                  {formErrors.name && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    البريد الإلكتروني <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className={inputClasses('email')}
                    placeholder="example@lab.com"
                    dir="ltr"
                  />
                  {formErrors.email && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رقم الجوال
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    className={inputClasses('phone')}
                    placeholder="05xxxxxxxx"
                    dir="ltr"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الدور الوظيفي <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.role_id}
                    onChange={e => setFormData({...formData, role_id: e.target.value})}
                    className={inputClasses('role_id')}
                  >
                    <option value="">اختر الدور</option>
                    {roles.map(role => (
                      <option key={role._id} value={role._id}>
                        {roleDisplayNames[role.name] || role.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.role_id && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.role_id}</p>
                  )}
                </div>

                {/* Branch / Contractor Lab */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {roles.find(r => r._id === formData.role_id)?.name === 'contractor_lab' ? 'المختبر المتعاقد' : 'الفرع'}
                  </label>
                  <select
                    value={formData.branch_id}
                    onChange={e => setFormData({...formData, branch_id: e.target.value})}
                    className={inputClasses('branch_id')}
                  >
                    <option value="">اختر {roles.find(r => r._id === formData.role_id)?.name === 'contractor_lab' ? 'المختبر المتعاقد' : 'الفرع'} (اختياري)</option>
                    {roles.find(r => r._id === formData.role_id)?.name === 'contractor_lab' ? (
                      // Show contractor labs for contractor_lab role
                      contractorLabs.map(lab => (
                        <option key={lab._id} value={lab._id}>{lab.name} - {lab.city}</option>
                      ))
                    ) : (
                      // Show branches for other roles
                      branches.map(branch => (
                        <option key={branch._id} value={branch._id}>{branch.branch_name}</option>
                      ))
                    )}
                  </select>
                </div>

                {/* Active Toggle (only for edit) */}
                {showEditModal && (
                  <div className="flex items-center gap-3 pt-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={e => setFormData({...formData, is_active: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <span className="text-sm text-gray-700">الحساب نشط</span>
                  </div>
                )}

                {/* Password Note for Add */}
                {showAddModal && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-sm text-blue-700">
                      <strong>ملاحظة:</strong> سيتم إنشاء كلمة مرور عشوائية للموظف الجديد. تأكد من إرسالها للموظف عبر البريد الإلكتروني.
                    </p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحفظ...</>
                  ) : (
                    showEditModal ? 'حفظ التغييرات' : 'إضافة الموظف'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Password Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPasswordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">تم إنشاء الحساب بنجاح</h3>
                <p className="text-gray-500 mb-4">
                  كلمة المرور المؤقتة للموظف:
                </p>
                
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200 mb-4">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={generatedPassword}
                    readOnly
                    className="flex-1 bg-transparent text-center font-mono text-lg focus:outline-none"
                    dir="ltr"
                  />
                  <button onClick={() => setShowPassword(!showPassword)} className="p-2 hover:bg-gray-200 rounded-lg">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={copyPassword} className="p-2 hover:bg-gray-200 rounded-lg text-blue-600">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>

                <p className="text-xs text-amber-600 mb-4">
                  ⚠️ احفظ كلمة المرور الآن. لن تتمكن من رؤيتها مرة أخرى.
                </p>

                <button 
                  onClick={() => { setShowPasswordModal(false); setGeneratedPassword(''); }}
                  className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700"
                >
                  تم
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">حذف الموظف</h3>
                <p className="text-gray-500 mb-6">
                  هل أنت متأكد من حذف حساب "{selectedUser?.name}"؟
                  <br />
                  <span className="text-sm text-red-500">هذا الإجراء لا يمكن التراجع عنه</span>
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">إلغاء</button>
                  <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحذف...</> : 'حذف'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Permissions Modal */}
      <AnimatePresence>
        {showPermissionsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPermissionsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-3xl shadow-xl max-h-[90vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between rounded-t-2xl flex-shrink-0">
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  صلاحيات الأدوار
                </h3>
                <button onClick={() => setShowPermissionsModal(false)} className="text-slate-400 hover:text-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                {/* Role Tabs */}
                <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-100">
                  {roles.map(role => (
                    <button
                      key={role._id}
                      onClick={() => loadRolePermissions(role)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedRole?._id === role._id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {role.name_ar || roleDisplayNames[role.name] || role.name}
                      {role.name === 'owner' && (
                        <span className="mr-1 text-xs opacity-70">(كل الصلاحيات)</span>
                      )}
                    </button>
                  ))}
                  {/* Add Role Button */}
                  <button
                    onClick={() => setShowAddRoleModal(true)}
                    className="px-3 py-2 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 flex items-center gap-1.5 transition-all"
                  >
                    <Plus className="w-4 h-4" strokeWidth={2.5} />
                    <span>إضافة دور</span>
                  </button>
                </div>

                {/* Owner Notice */}
                {selectedRole?.name === 'owner' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
                    <p className="text-sm text-blue-700">
                      <strong>ملاحظة:</strong> مدير النظام (Owner) لديه صلاحية الوصول لجميع الصفحات تلقائياً ولا يمكن تعديل صلاحياته.
                    </p>
                  </div>
                )}

                {/* Select All Toggle */}
                {selectedRole?.name !== 'owner' && (
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-600">
                      تم تحديد <span className="font-semibold text-slate-900">{rolePermissions.length}</span> من <span className="font-semibold text-slate-900">{availablePages.length}</span> صفحة
                    </span>
                    <button
                      onClick={toggleAllPermissions}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {rolePermissions.length === availablePages.length ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                    </button>
                  </div>
                )}

                {/* Pages Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availablePages.map(page => {
                    const isEnabled = selectedRole?.name === 'owner' || rolePermissions.includes(page.key);
                    const isOwner = selectedRole?.name === 'owner';
                    
                    return (
                      <div
                        key={page.key}
                        onClick={() => !isOwner && togglePagePermission(page.key)}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                          isOwner
                            ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
                            : isEnabled
                            ? 'bg-blue-50 border-blue-200 cursor-pointer hover:bg-blue-100'
                            : 'bg-white border-gray-200 cursor-pointer hover:bg-gray-50'
                        }`}
                      >
                        <div className={`flex-shrink-0 ${isOwner ? 'text-gray-400' : isEnabled ? 'text-blue-600' : 'text-gray-300'}`}>
                          {isEnabled ? (
                            <CheckSquare className="w-5 h-5" strokeWidth={2} />
                          ) : (
                            <Square className="w-5 h-5" strokeWidth={2} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm ${isOwner ? 'text-gray-500' : 'text-slate-900'}`}>
                            {page.name_ar}
                          </p>
                          <p className="text-xs text-gray-500 truncate" dir="ltr">
                            {page.path}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Footer - Fixed at bottom */}
              <div className="px-6 py-4 border-t border-gray-100 flex justify-between flex-shrink-0 bg-white rounded-b-2xl">
                <div>
                  {/* Delete Role Button - only for custom roles */}
                  {selectedRole && !systemRoles.includes(selectedRole.name) && selectedRole.name !== 'owner' && (
                    <button
                      onClick={handleDeleteRole}
                      disabled={deletingRole}
                      className="px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-xl font-medium flex items-center gap-2 border border-red-200"
                    >
                      {deletingRole ? (
                        <><div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />جاري الحذف...</>
                      ) : (
                        <><Trash2 className="w-4 h-4" />حذف الدور</>
                      )}
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPermissionsModal(false)}
                    className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                  >
                    إغلاق
                  </button>
                  {selectedRole?.name !== 'owner' && (
                    <button
                      onClick={savePermissions}
                      disabled={savingPermissions}
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingPermissions ? (
                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحفظ...</>
                      ) : (
                        'حفظ الصلاحيات'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Role Modal */}
      <AnimatePresence>
        {showAddRoleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
            onClick={() => setShowAddRoleModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  إضافة دور جديد
                </h3>
                <button onClick={() => setShowAddRoleModal(false)} className="text-slate-400 hover:text-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Name Arabic */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم الدور بالعربي <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newRoleData.name_ar}
                    onChange={e => setNewRoleData({...newRoleData, name_ar: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="مثال: فني مختبر"
                  />
                </div>

                {/* Name English */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم الدور بالإنجليزي
                  </label>
                  <input
                    type="text"
                    value={newRoleData.name_en}
                    onChange={e => setNewRoleData({...newRoleData, name_en: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Example: Lab Technician"
                    dir="ltr"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الوصف
                  </label>
                  <textarea
                    value={newRoleData.description}
                    onChange={e => setNewRoleData({...newRoleData, description: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:border-blue-500 focus:ring-blue-500 resize-none"
                    placeholder="وصف مختصر للدور (اختياري)"
                    rows={3}
                  />
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-700">
                    <strong>ملاحظة:</strong> بعد إضافة الدور، يمكنك تحديد الصلاحيات والصفحات المسموح بها له.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddRoleModal(false)}
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleAddRole}
                  disabled={savingRole || !newRoleData.name_ar.trim()}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {savingRole ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الإضافة...</>
                  ) : (
                    'إضافة الدور'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default StaffManagement;

